import {cn} from 'src/lib/utils';

/**
 * Whole units of `currency`, but cents for an amount under one unit, which
 * whole units would round to nothing; a code Intl doesn't know reads as a
 * suffix.
 */
export const formatMoney = (cents: number, currency: string): string => {
  const fraction = cents !== 0 && Math.abs(cents) < 100 ? 2 : 0;
  const amount = fraction ? cents / 100 : Math.round(cents / 100);
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: fraction,
      maximumFractionDigits: fraction,
    }).format(amount);
  } catch {
    return `${amount.toLocaleString('en-US', {minimumFractionDigits: fraction})} ${currency}`;
  }
};

export const formatSigned = (cents: number, currency: string): string =>
  `${cents < 0 ? '−' : '+'}${formatMoney(Math.abs(cents), currency)}`;

/** Green for a gain, red for a loss, nothing when unknown. */
export const gainClass = (cents: number | null | undefined) =>
  cn(
    'tabular-nums',
    cents != null && cents > 0 && 'text-positive',
    cents != null && cents < 0 && 'text-destructive',
  );

/** Four decimals, or six significant digits for a fraction smaller than one. */
export const formatQuantity = (quantity: number): string =>
  Math.abs(quantity) < 1 && quantity !== 0
    ? quantity.toLocaleString('en-US', {maximumSignificantDigits: 6})
    : quantity.toLocaleString('en-US', {maximumFractionDigits: 4});

/** A signed share count: "+2 sh", "−0.07 sh". */
export const formatShares = (quantity: number): string =>
  `${quantity < 0 ? '−' : '+'}${formatQuantity(Math.abs(quantity))} sh`;
